/**
 * Sprint 22 Chunk 5 — single source of truth for stage labels,
 * variants, and operator-allowed transitions on the sales-inbox
 * commercial pipeline.
 *
 * Mirrors the backend constants in
 * `middleware/app/models/sales_order_intake.py`. Test
 * (`stage-meta.test.ts`) pins the contract so future drift between
 * frontend + backend trips a unit test.
 */

import type { Stage, ExtractionState } from '../api/types'

type ChipVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info'

/**
 * Canonical ordered list — used by kanban column ordering + the
 * status-filter dropdown so both surfaces walk the pipeline in the
 * same direction.
 */
export const STAGES: readonly Stage[] = [
  'new_customer_po',
  'carla_created',
  'sales_quote_created',
  'needs_review',
  'revised_customer_po_requested',
  'need_vendor_information',
  'need_customer_payment',
  'order_acknowledged_needs_followup',
  'pending_ship_date_confirmation',
  'sales_order_confirmed_and_sent',
  'sales_order_cancelled',
] as const

export const TERMINAL_STAGES: readonly Stage[] = [
  'sales_order_confirmed_and_sent',
  'sales_order_cancelled',
] as const

/**
 * Stages shown on the C&C board (kanban columns + status filter).
 *
 * C&C overhaul — the sales inbox surfaces NEW orders needing handling. The
 * two terminal stages are NO LONGER board columns: once the linked Odoo SO
 * is confirmed/cancelled the card is archived off the board (Odoo owns it
 * from there). The terminal stage values still exist for historical rows +
 * stage-history rendering; they're just never columns or drag targets.
 */
export const BOARD_STAGES: readonly Stage[] = STAGES.filter(
  (s) => !TERMINAL_STAGES.includes(s as (typeof TERMINAL_STAGES)[number]),
) as readonly Stage[]

export const STAGE_LABELS: Record<Stage, string> = {
  new_customer_po: 'New Customer PO',
  carla_created: 'CARLA Created',
  sales_quote_created: 'Sales Quote Created',
  needs_review: 'Needs Review',
  revised_customer_po_requested: 'Revised Customer PO Requested',
  need_vendor_information: 'Need Vendor Information',
  need_customer_payment: 'Need Customer Payment',
  order_acknowledged_needs_followup: 'Order Acknowledged — Needs Follow-up',
  pending_ship_date_confirmation: 'Pending Ship Date Confirmation',
  sales_order_confirmed_and_sent: 'Sales Order Confirmed and Sent',
  sales_order_cancelled: 'Sales Order Cancelled',
}

/**
 * Short kanban-column / chip label. Tighter than the canonical label
 * so column headers stay readable.
 */
export const STAGE_SHORT_LABELS: Record<Stage, string> = {
  new_customer_po: 'New PO',
  carla_created: 'CARLA Created',
  sales_quote_created: 'Quote Created',
  needs_review: 'Needs Review',
  revised_customer_po_requested: 'Revised PO Requested',
  need_vendor_information: 'Need Vendor Info',
  need_customer_payment: 'Need Payment',
  order_acknowledged_needs_followup: 'Need Follow-up',
  pending_ship_date_confirmation: 'Pending Ship Date',
  sales_order_confirmed_and_sent: 'Confirmed & Sent',
  sales_order_cancelled: 'Cancelled',
}

export const STAGE_VARIANT: Record<Stage, ChipVariant> = {
  new_customer_po: 'neutral',
  carla_created: 'info',
  sales_quote_created: 'info',
  needs_review: 'warning',
  revised_customer_po_requested: 'warning',
  need_vendor_information: 'warning',
  need_customer_payment: 'warning',
  order_acknowledged_needs_followup: 'warning',
  pending_ship_date_confirmation: 'warning',
  sales_order_confirmed_and_sent: 'success',
  sales_order_cancelled: 'danger',
}

/**
 * Operator-driven transitions — permissive table mirroring the Python
 * ALLOWED_TRANSITIONS frozenset 1:1.
 *
 * Pattern (revised 2026-05-08 v3):
 *   - Any non-terminal stage can drag to any other stage (incl. either
 *     terminal). Lateral moves, undo to earlier stages, jump to confirm
 *     or cancel — all allowed.
 *   - Terminals can ONLY undo to needs_review. Cross-terminal moves
 *     (cancelled ↔ confirmed_and_sent) are excluded — operator should
 *     undo first then re-apply the other terminal.
 *   - The Odoo reconciler still auto-drives terminals when sale.order
 *     state flips; operator transitions are additive (idempotent).
 *
 * Derived programmatically rather than hand-listed so the table stays
 * in sync with `STAGES` + `TERMINAL_STAGES` if those ever change.
 */
export interface Transition {
  from: Stage
  to: Stage
}

// C&C overhaul — board transitions are board-stage → board-stage only.
// Operators no longer drag to terminal stages; the Odoo reconciler/webhook
// archives a card when its linked SO is confirmed/cancelled.
export const ALLOWED_TRANSITIONS: readonly Transition[] = [
  ...BOARD_STAGES.flatMap((from) =>
    BOARD_STAGES.filter((to) => to !== from).map<Transition>((to) => ({
      from,
      to,
    })),
  ),
] as const

export function isTerminal(stage: Stage): boolean {
  return TERMINAL_STAGES.includes(stage as (typeof TERMINAL_STAGES)[number])
}

export function transitionAllowed(from: Stage, to: Stage): boolean {
  return ALLOWED_TRANSITIONS.some((t) => t.from === from && t.to === to)
}

export function allowedTargetsFrom(stage: Stage): Stage[] {
  return ALLOWED_TRANSITIONS.filter((t) => t.from === stage).map((t) => t.to)
}

// ─── extraction_state meta ─────────────────────────────────────────

export const EXTRACTION_STATE_LABELS: Record<ExtractionState, string> = {
  pending: 'Queued',
  running: 'Extracting…',
  complete: 'Extracted',
  failed: 'Extraction Failed',
}

export const EXTRACTION_STATE_VARIANT: Record<ExtractionState, ChipVariant> = {
  pending: 'neutral',
  running: 'info',
  complete: 'neutral',
  failed: 'danger',
}

/**
 * Detect whether a stage_history entry is a synthetic extraction
 * marker (e.g. `extraction:running`) vs. a real commercial transition.
 *
 * Sprint 33 — stage can be null on the new anomaly-entry shape
 * (`{kind:'anomaly', ...}`). Null-safe here so renderers can call this
 * without pre-checking; null returns false (anomaly entries render
 * via their own branch).
 */
export function isExtractionMarker(stage: string | null | undefined): boolean {
  return typeof stage === 'string' && stage.startsWith('extraction:')
}

/**
 * Pretty-print the extraction marker as e.g. `"Extracting…"` so the
 * timeline reads consistently.
 */
export function extractionMarkerLabel(stage: string): string {
  const state = stage.replace(/^extraction:/, '') as ExtractionState
  return EXTRACTION_STATE_LABELS[state] ?? stage
}

/**
 * Friendly label for a CARLA-flagged anomaly type. Stays close to the
 * backend enum names so operators can map back to the raw record.
 */
const ANOMALY_TYPE_LABELS: Record<string, string> = {
  new_customer: 'New customer',
  new_product: 'New product',
  price_mismatch: 'Price mismatch',
  missing_po_number: 'Missing PO #',
  duplicate_so: 'Possible duplicate SO',
  ambiguous_match: 'Ambiguous match',
  data_quality: 'Data quality',
}

export function anomalyTypeLabel(anomalyType: string | null | undefined): string {
  if (!anomalyType) return 'Anomaly'
  return ANOMALY_TYPE_LABELS[anomalyType] ?? anomalyType
}
